<template>
  <div class="tw-hidden md:tw-block tw-relative" ref="container">
    <FormField
      v-model="search"
      :placeholder="$t('common.global-search-placeholder')"
      :left-icon="$i18n.locale == 'ar' ? '' : 'clarity:search-line'"
      :right-icon="$i18n.locale == 'ar' ? 'clarity:search-line' : isLoading ? 'eos-icons:three-dots-loading' : ''"
      :hide-label="true"
      :hide-errors="true"
      type="text"
      class="tw-min-w-[400px]"
      @input="debouncedSearch"
    />
    
    <transition
      class="tw-duration-200"
      enter-from-class="tw-translate-y-[10px] tw-opacity-0"
      leave-to-class="tw-translate-y-[10px] tw-opacity-0"
    >
      <div v-if="showResults" class="tw-duration-100">
        <div class="tw-absolute tw-z-30 tw-top-[calc(100%+5px)] tw-left-0 tw-right-0 tw-flex tw-flex-col tw-w-full tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-shadow-lg">
          <!-- Loading indicator -->
          <div v-if="isLoading" class="tw-p-6 tw-text-center tw-text-gray-500 tw-flex tw-flex-col tw-items-center tw-gap-2">
            <icon icon="line-md:loading-twotone-loop" class="tw-text-2xl tw-text-primary-500" />
            <p>{{ $t('common.searching') }}</p>
          </div>

          <!-- No results message - only show when not loading and no results -->
          <div v-if="!isLoading && (!results.orders || results.orders.length === 0) && (!results.products || results.products.length === 0)" class="tw-p-6 tw-text-center tw-text-gray-500 tw-flex tw-flex-col tw-items-center tw-gap-2">
            <icon icon="solar:magnifer-linear" class="tw-text-2xl tw-text-gray-400" />
            <p>{{ $t('common.no_results_found') }}</p>
          </div>

          <div v-if="!isLoading && (results.orders?.length > 0 || results.products?.length > 0)" class="tw-flex tw-flex-col">
            <!-- Tabs -->
            <div class="tw-flex tw-border-b tw-border-gray-200">
              <button 
                @click="activeTab = 'orders'" 
                :class="[activeTab === 'orders' ? 'tw-border-b-2 tw-border-primary-500 tw-text-primary-500 tw-bg-primary-50' : 'tw-text-gray-500 hover:tw-bg-gray-50']"
                class="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-justify-center tw-transition-colors tw-duration-200"
              >
                <icon icon="solar:bag-check-bold-duotone" class="tw-text-lg" />
                <span>{{ $t('common.orders') }}</span>
                <span v-if="results.orders?.length" class="tw-ml-1 tw-bg-primary-100 tw-text-primary-700 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs">
                  {{ results.orders.length }}
                </span>
              </button>
              <button 
                @click="activeTab = 'products'" 
                :class="[activeTab === 'products' ? 'tw-border-b-2 tw-border-primary-500 tw-text-primary-500 tw-bg-primary-50' : 'tw-text-gray-500 hover:tw-bg-gray-50']"
                class="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-justify-center tw-transition-colors tw-duration-200"
              >
                <icon icon="solar:box-bold-duotone" class="tw-text-lg" />
                <span>{{ $t('common.products') }}</span>
                <span v-if="results.products?.length" class="tw-ml-1 tw-bg-primary-100 tw-text-primary-700 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs">
                  {{ results.products.length }}
                </span>
              </button>
            </div>

            <!-- Tab content -->
            <div class="tw-max-h-[450px] tw-overflow-y-auto tw-p-1">
              <!-- Orders Tab -->
              <div v-if="activeTab === 'orders'" class="tw-p-2">
                <div v-for="order in results.orders" :key="'order-'+order.id" 
                  class="tw-p-3 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-mb-2 tw-border tw-border-gray-200 tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-shadow-md"
                  @click="navigateTo('order', order.id, order)">
                  <!-- Order header -->
                  <div class="tw-flex tw-justify-between tw-items-center tw-gap-2">
                    <div class="tw-font-medium tw-text-base tw-text-gray-700 tw-flex tw-items-center">
                      <span class="tw-bg-primary-100 tw-text-primary-700 tw-rounded-md tw-px-2 tw-py-1 ltr:tw-mr-2 rtl:tw-ml-2">#{{ order.id }}</span>
                      <span class="tw-truncate">{{ order.customer_name }}</span>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
                      <!-- Nawris code badge -->
                      <div v-if="order.external_id" class="tw-text-xs tw-bg-blue-100 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-whitespace-nowrap">
                        {{ order.external_id }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Order details -->
                  <div class="tw-flex tw-justify-between tw-items-start tw-mt-2 tw-gap-3">
                    <div class="tw-space-y-1 ltr:tw-pr-2 rtl:tw-pl-2">
                      <div class="tw-text-sm tw-text-gray-600 tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:phone-bold-duotone" class="tw-text-gray-400" />
                        <span class="tw-truncate">{{ order.customer_phone }}</span>
                      </div>
                      <div class="tw-text-sm tw-text-gray-600 tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:map-point-bold-duotone" class="tw-text-gray-400" />
                        <span class="tw-truncate">{{ order.customer_city }}</span>
                      </div>
                    </div>
                    <div class="tw-text-xs tw-text-gray-500 tw-flex tw-flex-col tw-items-end tw-shrink-0">
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:calendar-bold-duotone" class="tw-text-gray-400" />
                        {{ formatDate(order.created_at) }}
                      </div>
                      <div v-if="order.items && order.items.length > 0" class="tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:box-bold-duotone" class="tw-text-gray-400" />
                        {{ order.items.length }} {{ order.items.length === 1 ? $t('common.item') : $t('common.items') }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Status information -->
                  <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
                    <!-- Agent status (replaces followup status) -->
                    <div v-if="order.agent_status" :class="[getAgentStatusColor(order.agent_status)]">
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:user-check-bold-duotone" class="tw-text-[10px]" />
                        {{ $t(`order-status.confirmation.${order.agent_status}`) || formatStatusKey(order.agent_status) }}
                      </div>
                    </div>
                    <!-- Delivery status -->
                    <div v-if="order.delivery_status" :class="[getDeliveryStatusColor(order.delivery_status)]">
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:truck-bold-duotone" class="tw-text-[10px]" />
                        {{ $t(`order-status.delivery.${order.delivery_status}`) || formatStatusKey(order.delivery_status) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Products Tab -->
              <div v-if="activeTab === 'products'" class="tw-p-2">
                <div v-for="product in results.products" :key="'product-'+product.id" 
                  class="tw-p-3 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-mb-2 tw-border tw-border-gray-200 tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-shadow-md" 
                  @click="navigateTo('product', product.id)">
                  <!-- Product header -->
                  <div class="tw-flex tw-justify-between tw-items-center">
                    <div class="tw-font-medium tw-text-base tw-text-gray-700 tw-truncate ltr:tw-pr-2 rtl:tw-pl-2">{{ product.name }}</div>
                    <div v-if="product.is_active" class="tw-text-xs tw-bg-green-100 tw-text-green-700 tw-px-2 tw-py-1 tw-rounded tw-whitespace-nowrap">
                      {{ $t('common.active') }}
                    </div>
                    <div v-else class="tw-text-xs tw-bg-gray-100 tw-text-gray-700 tw-px-2 tw-py-1 tw-rounded tw-whitespace-nowrap">
                      {{ $t('common.inactive') }}
                    </div>
                  </div>
                  
                  <!-- Product details -->
                  <div class="tw-flex tw-justify-between tw-items-start tw-mt-2 tw-gap-3">
                    <div class="tw-space-y-1">
                      <div class="tw-text-sm tw-text-gray-600 tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:tag-price-bold-duotone" class="tw-text-gray-400" />
                        {{ $t('fields.product-sku') }}: <span class="tw-font-medium">{{ product.sku || '-' }}</span>
                      </div>
                      <div class="tw-text-sm tw-text-gray-600 tw-flex tw-items-center tw-gap-1">
                        <icon icon="solar:dollar-bold-duotone" class="tw-text-gray-400" />
                        {{ $t('common.price') }}: <span class="tw-font-medium">{{ product.selling_price }}</span>
                      </div>
                    </div>
                    <div :class="{'tw-text-red-500': product.quantity < (product.stock_alert || 0), 'tw-text-green-600': product.quantity >= (product.stock_alert || 0)}"
                      class="tw-text-sm tw-flex tw-items-center tw-gap-1 tw-font-medium tw-shrink-0">
                      <icon :icon="product.quantity < (product.stock_alert || 0) ? 'solar:box-minimalistic-broken' : 'solar:box-minimalistic-bold'" />
                      {{ product.quantity }}
                    </div>
                  </div>
                  
                  <!-- Performance metrics -->
                  <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2 tw-mt-3 tw-p-2 tw-bg-gray-50 tw-rounded-md">
                    <div class="tw-text-sm tw-flex tw-items-center tw-gap-1">
                      <icon icon="solar:shopping-cart-check-bold-duotone" class="tw-text-primary-400" />
                      <span class="tw-text-gray-700">{{ $t('common.orders') }}:</span>
                      <span class="tw-font-medium tw-text-gray-900">{{ product.order_count || 0 }}</span>
                    </div>
                    <div class="tw-text-sm tw-flex tw-items-center tw-gap-1">
                      <icon icon="solar:box-minimalistic-bold-duotone" class="tw-text-primary-400" />
                      <span class="tw-text-gray-700">{{ $t('common.available') }}:</span>
                      <span :class="{'tw-text-red-500': product.available_quantity < 0, 'tw-text-gray-900': product.available_quantity >= 0}"
                        class="tw-font-medium">{{ product.available_quantity || 0 }}</span>
                    </div>
                    <div class="tw-text-sm tw-flex tw-items-center tw-gap-1">
                      <icon icon="solar:checkmark-circle-bold-duotone" class="tw-text-primary-400" />
                      <span class="tw-text-gray-700">{{ $t('common.confirmation_rate') }}:</span>
                      <span class="tw-font-medium tw-text-gray-900">{{ product.confirmation_rate || 0 }}%</span>
                    </div>
                    <div class="tw-text-sm tw-flex tw-items-center tw-gap-1">
                      <icon icon="solar:truck-bold-duotone" class="tw-text-primary-400" />
                      <span class="tw-text-gray-700">{{ $t('common.delivery_rate') }}:</span>
                      <span class="tw-font-medium tw-text-gray-900">{{ product.delivery_rate || 0 }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Update Order Popup -->
    <transition>
      <div v-if="showUpdateOrderPopup">
        <update-order-popup 
          v-model:visible="showUpdateOrderPopup" 
          :item="selectedOrder" 
          @update="handleOrderUpdate" 
        />
      </div>
    </transition>
  </div>
</template>

<script setup>
import FormField from "@/components/form/FormField";
import { ref, onBeforeUnmount } from "vue";
import { server } from "@/api";
import { useRouter } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import { order_deliveries, order_confirmations } from '@/config/status';
import UpdateOrderPopup from '@/components/orders/UpdateOrderPopup';

const search = ref("");
const results = ref({ orders: [], products: [] });
const isLoading = ref(false);
const showResults = ref(false);
const container = ref(null);
const router = useRouter();
const searchTimeout = ref(null);
const activeTab = ref("orders");

// For order edit popup
const showUpdateOrderPopup = ref(false);
const selectedOrder = ref(null);

// Debounce search to prevent excessive API calls
const debouncedSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  
  if (search.value.trim().length < 2) {
    results.value = { orders: [], products: [] };
    showResults.value = false;
    return;
  }
  
  searchTimeout.value = setTimeout(() => {
    performSearch();
  }, 300);
};

// Handle search API call
const performSearch = async () => {
  if (search.value.trim().length < 2) return;
  
  isLoading.value = true;
  showResults.value = true;
  
  try {
    const response = await server().get("/api/search", {
      params: { search: search.value }
    });
    
    results.value = response.data || { orders: [], products: [] };

  } catch (error) {
    console.error("Search error:", error);
    results.value = { orders: [], products: [] };
  } finally {
    isLoading.value = false;
    
    // Set active tab based on results
    if (results.value.orders && results.value.orders.length > 0) {
      activeTab.value = "orders";
    } else if (results.value.products && results.value.products.length > 0) {
      activeTab.value = "products";
    }
  }
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Format status key for display
const formatStatusKey = (key) => {
  if (!key) return '';
  return key.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get agent status color (for confirmation status)
const getAgentStatusColor = (status) => {
  const confirmationStatus = order_confirmations.find(s => s.value === status);
  return confirmationStatus ? confirmationStatus.color + ' tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium' : 'tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium';
};

// Get delivery status color from status.js config
const getDeliveryStatusColor = (status) => {
  const deliveryStatus = order_deliveries.find(s => s.value === status);
  return deliveryStatus ? deliveryStatus.color + ' tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium' : 'tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium';
};

// Navigate to order or product details
const navigateTo = (type, id, item = null) => {
  if (type === 'order') {
    selectedOrder.value = item;
    showUpdateOrderPopup.value = true;
  } else if (type === 'product') {
    router.push({ name: 'pages.admin.products.show', params: { id } });
  }
  
  showResults.value = false;
  search.value = "";
};

// Handle order update from popup
const handleOrderUpdate = (updatedOrder) => {
  // Find the order in results and update it
  const orderIndex = results.value.orders.findIndex(o => o.id === updatedOrder.id);
  if (orderIndex !== -1) {
    results.value.orders[orderIndex] = updatedOrder;
  }
};

// Close results when clicking outside
onClickOutside(container, () => {
  showResults.value = false;
});

// Clean up on component unmount
onBeforeUnmount(() => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
});
</script>

<style></style>
